import React, { useState } from "react";
import { Loader2, RefreshCw, Upload, MessageSquare, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATE = {
  uploaded: { label: "Uploaded to lender", cls: "text-emerald-400 border-emerald-500/40", Icon: CheckCircle2 },
  on_file: { label: "On file", cls: "text-emerald-400 border-emerald-500/40", Icon: CheckCircle2 },
  missing: { label: "Missing \u2014 borrower asked", cls: "text-amber-400 border-amber-500/40", Icon: Upload },
  answer: { label: "Answer in lender portal", cls: "text-sky-400 border-sky-500/40", Icon: MessageSquare },
};

function NeedRow({ rid, n, onChanged }) {
  const st = STATE[n.state] || STATE.missing;
  const toggle = async (v) => {
    try { await api.put(`/admin/lender-submissions/${rid}/needs/${n.id}`, { done: !!v }); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="flex items-start gap-3 py-2 border-t border-edge/60" data-testid={`need-${rid}-${n.id}`}>
      <Checkbox checked={!!n.done} onCheckedChange={toggle} className="mt-0.5" data-testid={`need-done-${rid}-${n.id}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-xs leading-relaxed ${n.done ? "text-faint line-through" : "text-white/85"}`}>{n.text}</p>
        <div className="flex flex-wrap items-center gap-2 mt-1">
          <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide border rounded px-1.5 py-0.5 ${st.cls}`}><st.Icon className="w-3 h-3" />{st.label}</span>
          {n.due && <span className="text-[10px] text-faint">Due {n.due}</span>}
          {n.template && <span className="text-[10px] text-gold">Lender template to complete</span>}
          {(n.files || []).length > 0 && <span className="text-[10px] text-faint truncate">{n.files.join(", ")}</span>}
        </div>
      </div>
    </div>
  );
}

export function NeedsChecklist({ r, onChanged }) {
  const [busy, setBusy] = useState(false);
  const needs = r.needs || [];
  const open = needs.filter((n) => !n.done && n.state !== "uploaded" && n.state !== "on_file").length;
  const recheck = async () => {
    setBusy(true);
    try { await api.post(`/admin/lender-submissions/${r.id}/review-needs`); toast.success("Reviewing the lender's needs list\u2026"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!["submitted", "confirmed"].includes(r.status) || r.test_send) return null;
  return (
    <div className="mt-3 rounded-lg border border-edge bg-obsidian/40 p-3" data-testid={`needs-checklist-${r.id}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-white flex items-center gap-1.5">
          {open > 0 ? <AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
          Next steps {needs.length > 0 && <span className="text-faint font-normal">{"\u00b7"} {open} open of {needs.length}{r.needs_source === "standard" ? " (standard checklist)" : ""}</span>}
        </p>
        <Button onClick={recheck} disabled={busy || r.needs_status === "reviewing"} size="sm" variant="outline" className="btn-outline-glass rounded-lg h-8 gap-1 text-xs" data-testid={`needs-recheck-${r.id}`}>
          {busy || r.needs_status === "reviewing" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Re-check &amp; upload
        </Button>
      </div>
      {r.needs_status === "error" && <p className="text-xs text-brandred mt-2" data-testid={`needs-error-${r.id}`}>Could not read the lender's list: {r.needs_error}</p>}
      {needs.length === 0 && r.needs_status !== "error" && <p className="text-xs text-faint mt-2">{r.needs_status === "reviewing" ? "Reviewing\u2026" : "Not reviewed yet."}</p>}
      {needs.map((n) => <NeedRow key={n.id} rid={r.id} n={n} onChanged={onChanged} />)}
    </div>
  );
}
